import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { createFuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.controller';
import { funeralHomeInfoListHookUrl } from '@shared/api/funeralHomeInfo/funeralHomeInfo.hook';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import AddressSearcher from '@shared/containers/AddressSearcher/AddressSearcher';
import { Coord } from '@shared/interfaces/map.interface';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAsyncFn } from 'react-use';

import { AdminFuneralHomeDnAssignPanel } from './AdminFuneralHomeDnAssignPanel';

export const ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID =
  'admin-funeral-home-info-create';

interface CreateFormInputs {
  name: string;
  phoneNumber: string;
  address: string;
  parkingAddress: string;
  coord: { x: string; y: string };
}

const defaultFormValues: CreateFormInputs = {
  name: '',
  phoneNumber: '',
  address: '',
  parkingAddress: '',
  coord: { x: '', y: '' },
};

interface Props {
  //* 생성 완료 후 콜백 (목록 새로고침 등)
  onCreated?: () => void;
}

//* 관리자가 신규 장례식장 정보를 생성할 때 사용하는 모달
export const AdminFuneralHomeInfoCreateModal: FC<Props> =
  function AdminFuneralHomeInfoCreateModal({ onCreated }: Props) {
    const notiInstance = useRecoilValue(notificationInstanceAtom);
    const [open, setOpen] = useRecoilState(
      simpleOpenAtomFamily(ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID),
    );
    const queryClient = useQueryClient();
    const { assetRole } = useAuth();

    //* 생성 완료 후 꽃집(DN) 연결 단계로 전환하기 위한 상태
    const [createdFuneralHomeInfo, setCreatedFuneralHomeInfo] =
      useState<FuneralHomeInfo | null>(null);

    const { register, handleSubmit, setValue, watch, reset } = useForm<CreateFormInputs>({
      defaultValues: defaultFormValues,
    });

    // 언마운트 없이 재사용되는 모달이므로 열 때마다 초기화
    useEffect(() => {
      if (open) {
        reset(defaultFormValues);
        setCreatedFuneralHomeInfo(null);
      }
    }, [open, reset]);

    const closeModal = useCallback(() => {
      reset(defaultFormValues);
      setCreatedFuneralHomeInfo(null);
      setOpen(false);
    }, [reset, setOpen]);

    const selectedAddress = watch('address');

    const onAddressChange = (addressItem: { address: string; coord?: Coord }) => {
      setValue('address', addressItem.address);
      if (addressItem.coord) setValue('coord', addressItem.coord);
    };

    const [{ loading }, submit] = useAsyncFn(
      async (data: CreateFormInputs) => {
        try {
          const { data: created } = await createFuneralHomeInfo(data, assetRole());
          queryClient.invalidateQueries(funeralHomeInfoListHookUrl);
          notificateSuccess(notiInstance, '장례식장 생성 완료');
          onCreated?.();

          const createdInfo = created as FuneralHomeInfo | undefined;
          if (createdInfo?._id) {
            //* 꽃집(DN) 연결 단계로 전환 (연결 안하면 화환 주문 목록이 안뜸)
            setCreatedFuneralHomeInfo(createdInfo);
          } else {
            closeModal();
          }
        } catch (err) {
          notificateError(notiInstance, '장례식장 생성 실패');
          console.error(err);
        }
      },
      [assetRole, queryClient, notiInstance, onCreated, closeModal],
    );

    const onSubmit: SubmitHandler<CreateFormInputs> = (data) => {
      submit(data);
    };

    //* 2단계: 생성된 장례식장에 부고브랜드별 꽃집(DN) 연결
    if (createdFuneralHomeInfo) {
      return (
        <Modal openId={ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID}>
          <div className="space-y-3 pt-2">
            <h2 className="text-16 font-bold">꽃집 연결 (DN)</h2>
            <p className="text-13">
              <span className="font-medium">{createdFuneralHomeInfo.name}</span> 생성
              완료. 이어서 꽃집을 연결하세요.
            </p>
            <AdminFuneralHomeDnAssignPanel
              funeralHomeInfoId={createdFuneralHomeInfo._id}
              onSaved={closeModal}
              onSkip={closeModal}
            />
          </div>
        </Modal>
      );
    }

    return (
      <Modal openId={ADMIN_FUNERAL_HOME_INFO_CREATE_MODAL_OPEN_ID}>
        <form className="space-y-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
          <h2 className="text-16 font-bold">장례식장 생성</h2>
          <div className="grid grid-cols-1 gap-3 text-sm">
            {/* Name */}
            <div className="space-y-1">
              <label className="font-medium">장례식장 이름</label>
              <TextField
                type="text"
                autoComplete="off"
                className="w-full"
                placeholder="장례식장 이름"
                {...register('name', { required: true })}
              />
            </div>
            {/* PhoneNumber */}
            <div className="space-y-1">
              <label className="font-medium">전화번호</label>
              <TextField
                type="text"
                autoComplete="off"
                className="w-full"
                placeholder="전화번호"
                {...register('phoneNumber')}
              />
            </div>
            {/* Address */}
            <div className="space-y-1">
              <label className="font-medium">주소검색</label>
              <AddressSearcher onChange={onAddressChange} />
              {selectedAddress && (
                <p className="pl-2 text-gray-600">선택된 주소: {selectedAddress}</p>
              )}
            </div>
            {/* parkingAddress */}
            <div className="space-y-1">
              <label className="font-medium">주차안내</label>
              <TextField
                type="text"
                autoComplete="off"
                className="w-full"
                placeholder="주차안내"
                {...register('parkingAddress')}
              />
            </div>
          </div>
          <div className="pt-2">
            <Button
              disabled={loading}
              type="submit"
              className="button-rectangle h-10 w-full rounded-md theme-text-3 theme-bg-main"
            >
              생성
            </Button>
          </div>
        </form>
        <LoadingModal open={loading} title="생성중" />
      </Modal>
    );
  };
